import { Controller } from '@hotwired/stimulus';

/**
 * Remote Modal Controller
 *
 * Fetches remote content and loads it into a Bootstrap modal.
 *
 * Usage:
 * ```html
 * <a href="/path/to/content"
 *    data-controller="remote-modal"
 *    data-remote-modal-target-value="#my_modal"
 *    data-action="click->remote-modal#load">
 *   Open Modal
 * </a>
 * ```
 *
 * The modal should have a .modal-body element where content will be loaded.
 */
export default class extends Controller {
  static values = {
    target: String
  };

  load(event) {
    event.preventDefault();

    const url = this.element.href;
    const modal = document.querySelector(this.targetValue);
    const modalBody = modal?.querySelector('.modal-body');

    if (!modal || !modalBody) {
      // eslint-disable-next-line no-console
      console.error('Remote modal: target modal or modal-body not found');
      return;
    }

    modalBody.innerHTML = '<div class="text-center p-4"><i class="fa fa-spinner fa-spin"></i> Loading...</div>';

    // Only show the modal if it's not already visible
    const isModalVisible = $(modal).hasClass('show') || $(modal).is(':visible');
    if (!isModalVisible) {
      $(modal).modal('show');
    }

    fetch(url, {
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        modalBody.innerHTML = html;

        // Reinitialize Bootstrap tabs in the newly loaded content
        $(modalBody).find('[data-toggle="tab"]').on('click', function (e) {
          e.preventDefault();
          $(this).tab('show');
        });

        // Set up delegated click handler for remote-modal links in the loaded content
        // This ensures links work even if Stimulus doesn't connect to dynamically loaded content
        this.setupDelegatedHandlers(modal, modalBody);

        // Dispatch a custom event for any other components that need to reinitialize
        modal.dispatchEvent(new CustomEvent('remote-modal:loaded', { bubbles: true }));
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Remote modal request failed:', error);
        modalBody.innerHTML = '<div class="alert alert-danger">Failed to load content. Please try again.</div>';
      });
  }

  /**
   * Set up delegated event handlers for remote-modal links in dynamically loaded content.
   * This is a fallback in case Stimulus doesn't connect controllers to the new elements.
   */
  setupDelegatedHandlers(modal, modalBody) {
    // Remove any existing delegated handlers to prevent duplicates
    $(modalBody).off('click.remoteModal');

    // Add delegated click handler for links with data-controller="remote-modal"
    $(modalBody).on('click.remoteModal', '[data-controller="remote-modal"]', (e) => {
      e.preventDefault();

      const link = e.currentTarget;
      const url = link.href;
      const targetSelector = link.dataset.remoteModalTargetValue;
      const targetModal = targetSelector ? document.querySelector(targetSelector) : modal;
      const targetModalBody = targetModal?.querySelector('.modal-body');

      if (!targetModal || !targetModalBody) {
        // eslint-disable-next-line no-console
        console.error('Remote modal: target modal or modal-body not found');
        return;
      }

      targetModalBody.innerHTML = '<div class="text-center p-4"><i class="fa fa-spinner fa-spin"></i> Loading...</div>';

      fetch(url, {
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Request failed: ${response.status}`);
          }
          return response.text();
        })
        .then((html) => {
          targetModalBody.innerHTML = html;

          // Reinitialize Bootstrap tabs
          $(targetModalBody).find('[data-toggle="tab"]').on('click', function (tabEvent) {
            tabEvent.preventDefault();
            $(this).tab('show');
          });

          // Recursively set up delegated handlers for the new content
          this.setupDelegatedHandlers(targetModal, targetModalBody);

          targetModal.dispatchEvent(new CustomEvent('remote-modal:loaded', { bubbles: true }));
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Remote modal request failed:', error);
          targetModalBody.innerHTML = '<div class="alert alert-danger">Failed to load content. Please try again.</div>';
        });
    });
  }
}
